import React, { useEffect, useMemo, useState } from "react";
import { ArrowRight, ExternalLink, History, RefreshCw } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { SOURCE_TYPE_META, fmtDate } from "@/components/ripeness/radarMeta";
import { SourceTypeBadge } from "@/components/ripeness/SourceBadges";

const TRIGGER_LABEL = { backfill: "Backfill", reevaluation: "Daily recheck", opportunity_batch: "New records", source_refresh: "Extra-source run" };

function TypeFilter({ counts, value, onChange }) {
    const total = Object.values(counts || {}).reduce((a, b) => a + b, 0);
    const btn = (k, label, n) => (
        <button key={k} onClick={() => onChange(k)} data-testid={`hist-filter-${k}`}
            className={`rounded-full border px-2 py-0.5 text-[11px] transition-colors ${value === k ? "border-sky-400/60 text-sky-200 bg-sky-400/10" : "border-slate-700 text-slate-400 hover:text-slate-200"}`}>
            {label} <span className="text-slate-500">{n}</span>
        </button>
    );
    return (
        <div className="flex flex-wrap gap-1 mb-2" data-testid="hist-filters">
            {btn("all", "All", total)}
            {Object.entries(counts || {}).map(([k, n]) => btn(k, SOURCE_TYPE_META[k]?.label || k, n))}
        </div>
    );
}

function EvidenceItem({ e, i }) {
    const title = e.title || e.summary || e.id;
    return (
        <li className="flex items-start gap-2 py-1.5 border-t border-slate-800/70 first:border-t-0 text-xs" data-testid={`hist-item-${i}`}>
            <SourceTypeBadge type={e.source_type} />
            <span className="font-mono text-slate-500 whitespace-nowrap w-20">{fmtDate(e.published_at)}</span>
            <div className="flex-1 min-w-0">
                {e.url ? (
                    <a href={e.url} target="_blank" rel="noreferrer" className="text-sky-300 hover:underline inline-flex items-center gap-1 max-w-full" data-testid={`hist-item-link-${i}`}>
                        <span className="truncate">{title}</span><ExternalLink className="w-2.5 h-2.5 shrink-0" />
                    </a>
                ) : <span className="text-slate-300 line-clamp-2">{title}</span>}
                {e.status ? <span className="ml-1 text-slate-500">· {String(e.status).toLowerCase().replace(/_/g, " ")}{e.phases?.length ? ` · P${e.phases.join("/")}` : ""}</span> : null}
            </div>
            <span className="text-[10px] text-slate-500 whitespace-nowrap" title={e.first_seen_estimated ? "Recorded before evidence history existed — first seen is estimated" : ""}>
                first seen {fmtDate(e.first_seen_at)}{e.first_seen_estimated ? "*" : ""}
            </span>
        </li>
    );
}

function Timeline({ events }) {
    if (!events?.length) return null;
    return (
        <div className="mt-3" data-testid="hist-timeline">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Score timeline</div>
            <ol className="border-l border-slate-700 ml-1.5 pl-3 space-y-1.5">
                {events.map((ev, i) => (
                    <li key={ev.evaluation_id} className="text-xs relative" data-testid={`hist-event-${i}`}>
                        <span className="absolute -left-[17px] top-1.5 w-2 h-2 rounded-full bg-slate-500" />
                        <span className="font-mono text-slate-500">{fmtDate(ev.evaluated_at)}</span>
                        <span className="text-slate-400"> · {TRIGGER_LABEL[ev.trigger] || ev.trigger || "evaluation"}</span>
                        <span className="ml-2 text-slate-200 inline-flex items-center gap-1">
                            {ev.ripeness_old ?? "—"} <ArrowRight className="w-3 h-3 text-slate-500" /> <b>{ev.ripeness_new}</b>
                            {ev.delta ? <span className={ev.delta > 0 ? "text-emerald-300" : "text-rose-300"}>({ev.delta > 0 ? "+" : ""}{ev.delta})</span> : null}
                        </span>
                        {ev.tier_old && ev.tier_old !== ev.tier_new ? <span className="ml-2 text-amber-300">{ev.tier_old} → {ev.tier_new}</span> : null}
                        {ev.new_evidence?.length ? <span className="ml-2 text-sky-300">+{ev.new_evidence.length} new evidence</span> : null}
                        {ev.changes?.length ? <span className="ml-2 text-slate-500">{ev.changes.length} change{ev.changes.length > 1 ? "s" : ""}</span> : null}
                    </li>
                ))}
            </ol>
        </div>
    );
}

/** Every evidence item the track ever collected + a timeline of score moves. */
export function EvidenceHistory({ trackId, reloadKey }) {
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);
    const [type, setType] = useState("all");
    useEffect(() => {
        let live = true;
        setData(null); setErr(null);
        api.get(`/v1/ripeness/tracks/${encodeURIComponent(trackId)}/evidence-history`, { params: { limit: 50 } })
            .then((r) => live && setData(r.data)).catch((e) => live && setErr(formatApiErrorDetail(e)));
        return () => { live = false; };
    }, [trackId, reloadKey]);
    const items = useMemo(() => (data?.evidence || []).filter((e) => type === "all" || e.source_type === type), [data, type]);
    return (
        <div className="mt-3 rounded-lg border border-slate-700/60 bg-slate-950/40 p-3" data-testid="hist-panel">
            <div className="text-xs uppercase tracking-wider text-slate-400 mb-2 inline-flex items-center gap-1.5"><History className="w-3.5 h-3.5" /> Evidence history
                {data ? <span className="normal-case tracking-normal text-slate-500" data-testid="hist-eval-count">· {data.evaluations} evaluation{data.evaluations === 1 ? "" : "s"}</span> : null}
            </div>
            {err ? <div className="text-xs text-rose-300" data-testid="hist-error">{err}</div> : null}
            {!data && !err ? <div className="text-xs text-slate-500 inline-flex items-center gap-1.5"><RefreshCw className="w-3 h-3 animate-spin" /> Loading…</div> : null}
            {data ? (
                <>
                    <TypeFilter counts={data.counts} value={type} onChange={setType} />
                    <ul className="max-h-80 overflow-y-auto pr-1" data-testid="hist-items">{items.map((e, i) => <EvidenceItem key={e.key} e={e} i={i} />)}</ul>
                    {!items.length ? <div className="text-xs text-slate-500 italic">No evidence of this type.</div> : null}
                    <Timeline events={data.events} />
                </>
            ) : null}
        </div>
    );
}
